import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import noop from 'lodash/noop';
import { v4 as getUUID } from 'uuid';
import InfiniteScroll from '@adsk/bim360-matrix-react-infinite-scroll';
import './_MultiSelectList.scss';
import objectMap from '@adsk/bim360-matrix-react-helpers/es/objectMap';
import isEqual from 'lodash/isEqual';
import Checkbox from '@adsk/bim360-matrix-react-checkbox';
import Label from '@adsk/bim360-matrix-react-label';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';

/**
 * @class MultiSelectList - List of checkboxes with multi selection and optional infinite scroll.
 **/
class MultiSelectList extends Component {
  constructor(props) {
    super(props);

    this.state = this.getOptions(props);

    this.sortByLabel = this.sortByLabel.bind(this);
    this.sortByLabelDescending = this.sortByLabelDescending.bind(this);
    this.sortByLabelAscending = this.sortByLabelAscending.bind(this);
    this.onChange = this.onChange.bind(this);

    this.clientId = getUUID();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.selectedOptions, nextProps.selectedOptions)) {
      this.refreshOptions(nextProps);
    }
  }

  isControlled(prop, props = this.props) {
    return prop in props;
  }

  getLabel(option, props = this.props) {
    return this.props.labelGetter(option, props);
  }

  getValue(option, props = this.props) {
    return this.props.valueGetter(option, props);
  }

  getOptions(props) {
    const selectedOptions = this.isControlled('selectedOptions', props)
      ? props.selectedOptions
      : props.defaultSelectedOptions;
    return {
      selectedValueMap: objectMap(selectedOptions, {
        keyMapper: ({ item }) => this.getValue(item),
        valueMapper: () => true
      })
    };
  }

  refreshOptions(props) {
    this.setState(this.getOptions(props));
  }

  sortByLabelDescending(a, b) {
    const labelA = this.getLabel(a) || '';
    const labelB = this.getLabel(b) || '';
    return labelB.localeCompare(labelA);
  }

  sortByLabelAscending(a, b) {
    const labelA = this.getLabel(a) || '';
    const labelB = this.getLabel(b) || '';
    return labelA.localeCompare(labelB);
  }

  sortByLabel(sortOrder) {
    switch (sortOrder) {
      case MultiSelectList.SortOrder.ASC:
        return this.sortByLabelAscending;
      case MultiSelectList.SortOrder.DESC:
        return this.sortByLabelDescending;
      default:
        return null;
    }
  }

  getAllOptions() {
    if (this.props.grouped) {
      // Implementing `flatMap` using `reduce` as IE11 doesn't support `flatMap`
      return this.props.options.map(group => group.options).reduce((x, y) => x.concat(y), []);
    }

    return this.props.topOptions
      ? [...this.props.topOptions, ...this.props.options]
      : this.props.options;
  }

  onChange(option) {
    const value = this.getValue(option);
    const selectionStatus = !this.state.selectedValueMap[value];
    const selectedValueMap = { ...this.state.selectedValueMap, [value]: selectionStatus };
    if (!this.isControlled('selectedOptions')) {
      this.setState({ selectedValueMap });
    }

    const allOptions = this.getAllOptions();
    const selectedOptions = allOptions.filter(option => selectedValueMap[this.getValue(option)]);

    this.props.onChange && this.props.onChange(selectedOptions, option, selectionStatus);
  }

  normalizeOptionProps(option, className) {
    const customProps = { ...this.props.getOptionProps({ option }) };

    if (customProps.className) {
      customProps.className = `${className} ${customProps.className}`;
    } else {
      customProps.className = className;
    }

    return customProps;
  }

  renderOption(option, index) {
    const value = this.getValue(option);
    const inputId = `MultiSelectList__id-${this.clientId}__option-${value}`;
    const isOptionDisabled = !!this.props.disabledOptions.find(option => option === value);
    const labelClassName = classNames('MultiSelectList__option-label', {
      'MultiSelectList__option-label--disabled': isOptionDisabled
    });
    const optionLabel = this.getLabel(option);

    const optionComponent = (
      <div
        {...this.normalizeOptionProps(option, 'MultiSelectList__option-container')}
        key={`option-${index}-${value}`}
      >
        <Checkbox
          onChange={() => this.onChange(option)}
          disabled={isOptionDisabled}
          checked={this.state.selectedValueMap[value]}
          id={inputId}
        />
        <Label htmlFor={inputId} title={optionLabel} className={labelClassName}>
          {this.props.itemRenderer ? this.props.itemRenderer(option) : optionLabel}
        </Label>
      </div>
    );

    const OptionRenderer = this.props.optionRenderer;
    return OptionRenderer ? (
      <OptionRenderer option={option} index={index}>
        {optionComponent}
      </OptionRenderer>
    ) : (
      optionComponent
    );
  }

  renderOptions(optionsList, topOptionsList, sortOrder) {
    let topOptions = [];
    if (topOptionsList && topOptionsList.length > 0) {
      //sort topOptionsList only if SortOrder is defined
      if (sortOrder !== MultiSelectList.SortOrder.NONE) {
        topOptionsList = topOptionsList.map(option => option).sort(this.sortByLabel(sortOrder));
      }
      topOptions = topOptionsList.map((option, index) => {
        return this.renderOption(option, index);
      });
      topOptions.push(<hr />);
    }
    //sort optionsList only if SortOrder is defined
    if (sortOrder !== MultiSelectList.SortOrder.NONE) {
      optionsList = optionsList.map(option => option).sort(this.sortByLabel(sortOrder));
    }
    const options = optionsList.map((option, index) => {
      return this.renderOption(option, index);
    });
    return topOptions.concat(options);
  }

  renderGroupHeader(group) {
    const renderer = this.props.groupHeaderActionRenderer;

    if (group.noHeader) {
      return null;
    }

    return (
      <div className="MultiSelectList__group-header" key={`group-${group.groupId}`}>
        <div>{group.title || ''}</div>
        <hr className="MultiSelectList__group-header-hr" />
        {renderer && renderer(group)}
      </div>
    );
  }

  renderGroups(options, sortOrder) {
    return options
      .filter(group => group.options.length > 0)
      .map(group =>
        [this.renderGroupHeader(group)].concat(this.renderOptions(group.options, null, sortOrder))
      );
  }

  render() {
    const classes = classNames('MultiSelectList', this.props.className);
    const options = this.props.options;
    return (
      <div className={classes}>
        <InfiniteScroll
          {...this.props.infiniteScrollProps}
          items={
            this.props.grouped
              ? this.renderGroups(options, this.props.sortOrder)
              : this.renderOptions(this.props.options, this.props.topOptions, this.props.sortOrder)
          }
          direction={InfiniteScroll.Direction.DOWN}
          showScrollToButton={false}
        />
      </div>
    );
  }
}

MultiSelectList.SortOrder = {
  ASC: 'acs',
  DESC: 'desc',
  NONE: 'none'
};

MultiSelectList.defaultProps = {
  labelKey: 'label',
  valueKey: 'value',
  valueGetter: (option, props) => option[props.valueKey],
  labelGetter: (option, props) => option[props.labelKey],
  defaultSelectedOptions: [],
  disabledOptions: [],
  getOptionProps: noop,
  sortOrder: MultiSelectList.SortOrder.ASC
};

MultiSelectList.propTypes = {
  /**
   * A custom className for the component
   */
  className: PropTypes.string,
  /**
   * Optional - List of options to display at the top of the list.
   * Each option in the array should contain a 'labelKey' prop and a 'valueKey' prop
   * If you pass this list you should emmit it from the options list.
   */
  topOptions: PropTypes.arrayOf(PropTypes.any),
  /**
   * List of options, grouped as objects.
   * Each object in the array represents a group and must contain 'title' and options.
   * Also, an optional 'groupId' is supported in order for the (also optional)
   * groupHeaderActionRenderer to identify which group header it's rendering.
   *
   * A plain non-grouped list of options is also supported. In that case, each option in the array
   * should contain a 'labelKey' prop and a 'valueKey' prop.
   */
  options: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        noHeader: PropTypes.bool,
        options: PropTypes.arrayOf(PropTypes.any).isRequired,
        groupId: PropTypes.string
      })
    ),
    PropTypes.arrayOf(PropTypes.any)
  ]),
  /**
   * Indicates whether the options passed were in the shape of groups or plain list.
   */
  grouped: PropTypes.bool,
  /**
   * A render prop which gets the group and renders the group header items,
   * excluding the header title.
   */
  groupHeaderActionRenderer: PropTypes.func,
  /**
   * An array values of options that will be disabled.
   * The values in the array correspond to the value in each option (either from `valueKey` or `valueGetter`).
   */
  disabledOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.symbol])
  ),
  /**
   * Optional - Default list of selected options.
   * Each option in the array should contain a 'labelKey' prop and a 'valueKey' prop
   */
  defaultSelectedOptions: PropTypes.arrayOf(PropTypes.any),
  /**
   * Optional - List of selected options - makes the component controlled
   * Each option in the array should contain a 'labelKey' prop and a 'valueKey' prop
   */
  selectedOptions: PropTypes.arrayOf(PropTypes.any),
  /**
   * Option's label key name (default - 'label')
   */
  labelKey: PropTypes.string,
  /**
   * Option's value key name (default - 'value')
   */
  valueKey: PropTypes.string,
  /**
   * A callback function for an option toggle event. Callback gets called with (currentlySelectedOptions, toggledOption, selectionStatus)
   */
  onChange: PropTypes.func,
  /**
   * Props to configure the infinite scroll
   */
  infiniteScrollProps: PropTypes.shape(InfiniteScroll.propTypes),
  /**
   * Selector function for getting the label of an option
   */
  labelGetter: PropTypes.func,
  /**
   * Selector function for getting the value of an option
   */
  valueGetter: PropTypes.func,
  /**
   * Callback that's called with each option and returns the props needed to be applied on the option container.
   * ({ option }) => Object
   */
  getOptionProps: PropTypes.func,
  /**
   * Optional - Default is ascending order.
   * The order that the options will be sorted by.
   */
  sortOrder: PropTypes.oneOf(objectValues(MultiSelectList.SortOrder)),
  /**
   * A render prop which gets the option and renders the list item excluding the checkbox.
   */
  itemRenderer: PropTypes.func,
  /**
   * A render prop which allows to render a custom option or a surrounding component to the original option
   * Useful for wrapping each option with a surrounding component, for example: Tooltip
   * ({ option, children }) => node
   */
  optionRenderer: PropTypes.func
};

export default MultiSelectList;
